import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import {
  Card,
  Currency,
  Empty,
  ErrorBanner,
  FormattedDate,
  Loading,
  PageHeader,
  Tag,
} from '../../components/ui'

// Total Income click-through (A20 / #449). Single source of truth for the
// "Income" card. Since #550 the endpoint returns TWO headlines: total_income is
// Raw Received (all money that landed in cash + bank — the eight source buckets
// sum to exactly this), and earned_income is operating income, which is what the
// listed entries compose (capital + internal transfers excluded from the list).
type SourceBuckets = { self: number; client: number; other: number; transfer: number }

type IncomeEntry = {
  id: string
  type: string
  amount: number
  date: number
  description: string | null
  source: string | null
  gst_percentage: number | null
  gst_amount: number | null
  category_name: string | null
}

type IncomeDrillDown = {
  total_income: number
  earned_income: number
  income_by_source: { cash: SourceBuckets; bank: SourceBuckets }
  entries: IncomeEntry[]
  pagination: { total: number; limit: number; offset: number }
}

export function IncomeDrillDownPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const q = useQuery<IncomeDrillDown>({
    queryKey: ['income-drill-down', projectId],
    enabled: Boolean(projectId),
    queryFn: () =>
      fetchData<IncomeDrillDown>(
        orgPath(`/projects/${projectId}/accounts/income-drill-down?limit=100`),
      ),
  })

  if (q.isLoading) return <Loading />
  if (q.isError) return <ErrorBanner error={q.error} />

  const d = q.data
  if (!d) return <Empty message="No income data yet." />

  const src = d.income_by_source

  return (
    <div className="space-y-6">
      <PageHeader
        title="Income drill-down"
        subtitle="Total income is every rupee received into cash and bank. The listed entries compose earned income, which leaves out capital and internal transfers."
      />

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card>
          <div className="text-xs uppercase tracking-wide text-surface-400">Total income</div>
          <div className="text-2xl font-semibold mt-1"><Currency amount={d.total_income} /></div>
          <div className="text-[11px] text-surface-400 mt-1">
            Money received — the source buckets below sum to exactly this.
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wide text-surface-400">Earned income</div>
          <div className="text-2xl font-semibold mt-1"><Currency amount={d.earned_income} /></div>
          <div className="text-[11px] text-surface-400 mt-1">
            Excludes owner capital (Self) and inter-project transfers (Site Transfer), so
            it can be lower than Total income.
          </div>
        </Card>
        <Card>
          <div className="text-xs uppercase tracking-wide text-surface-400">Composing entries</div>
          <div className="text-2xl font-semibold mt-1">{d.pagination.total}</div>
        </Card>
      </div>

      <Card>
        <h2 className="font-semibold mb-3">By source</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-surface-400 border-b border-surface-700">
                <th className="py-2 pr-4">Landing</th>
                <th className="py-2 pr-4 text-right">Self</th>
                <th className="py-2 pr-4 text-right">Client</th>
                <th className="py-2 pr-4 text-right">Other</th>
                <th className="py-2 text-right">Site Transfer</th>
              </tr>
            </thead>
            <tbody>
              {(['cash', 'bank'] as const).map((landing) => {
                const b = src[landing]
                return (
                  <tr key={landing} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4 capitalize">{landing}</td>
                    <td className="py-2 pr-4 text-right"><Currency amount={b.self} /></td>
                    <td className="py-2 pr-4 text-right"><Currency amount={b.client} /></td>
                    <td className="py-2 pr-4 text-right"><Currency amount={b.other} /></td>
                    <td className="py-2 text-right"><Currency amount={b.transfer} /></td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <Card>
        <h2 className="font-semibold mb-3">Entries</h2>
        {d.entries.length === 0 ? (
          <Empty message="No composing entries." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-surface-400 border-b border-surface-700">
                  <th className="py-2 pr-4">Date</th>
                  <th className="py-2 pr-4">Category</th>
                  <th className="py-2 pr-4">Type</th>
                  <th className="py-2 pr-4">Description</th>
                  <th className="py-2 pr-4 text-right">GST</th>
                  <th className="py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {d.entries.map((e) => (
                  <tr key={e.id} className="border-b border-surface-700/50">
                    <td className="py-2 pr-4 whitespace-nowrap"><FormattedDate ts={e.date} /></td>
                    <td className="py-2 pr-4">{e.category_name ?? '—'}</td>
                    <td className="py-2 pr-4">
                      <Tag>{e.type.replace(/_/g, ' ')}</Tag>
                    </td>
                    <td className="py-2 pr-4 text-surface-300">{e.description ?? '—'}</td>
                    <td className="py-2 pr-4 text-right text-surface-400">
                      {e.gst_amount ? <Currency amount={e.gst_amount} /> : '—'}
                    </td>
                    <td className="py-2 text-right font-medium"><Currency amount={e.amount} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {d.pagination.total > d.entries.length && (
          <p className="text-xs text-surface-400 mt-3">
            Showing {d.entries.length} of {d.pagination.total} entries.
          </p>
        )}
      </Card>
    </div>
  )
}
